// frontend/src/pages/dashboards/FilterBar.jsx
// Phase 4 step 14b-2: the bar of filters above a dashboard's grid.
//
// One button per filter, saying what it is set to ("Period: Last 30 days");
// it opens a small panel to change it. A change applies at once: the page
// keeps the values in the URL (?f=) and every chart tile refetches, 250 ms
// after the last change. "Reset" goes back to the dashboard's defaults.
// In edit mode each filter can be edited or removed, and filters added; the
// definitions are saved with the dashboard, the values never are.
import React, { useEffect, useMemo, useState } from 'react';
import { Popover } from 'radix-ui';
import { Check, ChevronDown, Loader2, Pencil, Plus, RotateCcw, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { fieldValues } from '../../charts/semanticApi';
import {
  MAX_FILTERS, MAX_VALUES, RELATIVE_PRESETS, isActive, mergeValues, valueSources, valueSummary,
} from './filterState';

const PANEL = 'z-50 w-[min(20rem,calc(100vw-2rem))] rounded-2xl bg-[var(--ui-panel)] p-3 text-[var(--ui-text)] shadow-[0_18px_48px_rgba(0,0,0,0.55)] ring-1 ring-white/5 focus:outline-none';
const FIELD = 'w-full rounded-xl bg-[var(--ui-inset)] px-3 py-2 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';
const OPTION = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] hover:bg-[var(--ui-inset-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';
const ICON_BUTTON = 'rounded-md p-1 text-[var(--ui-text-muted)] hover:bg-[var(--ui-inset-hover)] hover:text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';
const SEARCH_DEBOUNCE_MS = 250;

function Footer({ onClear, active }) {
  return (
    <div className="mt-2 flex justify-end border-t border-[var(--ui-divider)] pt-2">
      <Button variant="ghost" size="sm" onClick={onClear} disabled={!active}>Clear</Button>
    </div>
  );
}

/* ---- date ---- */

function DatePanel({ value, onChange }) {
  const range = value && typeof value === 'object' ? value : {};
  const [from, setFrom] = useState(range.from || '');
  const [to, setTo] = useState(range.to || '');
  const bad = from && to && from > to;
  const applyRange = () => { if (!bad && (from || to)) onChange({ ...(from ? { from } : {}), ...(to ? { to } : {}) }); };
  return (
    <div>
      <div className="grid grid-cols-2 gap-0.5" role="group" aria-label="Periods">
        {RELATIVE_PRESETS.map(([key, label]) => (
          <button key={key} type="button" className={OPTION} aria-pressed={value === key} onClick={() => onChange(key)}>
            <Check className={`h-3.5 w-3.5 shrink-0 ${value === key ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>
      <div className="mt-2 border-t border-[var(--ui-divider)] pt-2">
        <p className="mb-1.5 text-xs text-[var(--ui-text-muted)]">Or between two days (either may be left empty)</p>
        <div className="flex items-center gap-2">
          <input type="date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} className={`${FIELD} [color-scheme:dark]`} />
          <span className="text-xs text-[var(--ui-text-muted)]">to</span>
          <input type="date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} className={`${FIELD} [color-scheme:dark]`} />
        </div>
        {bad && <p className="mt-1 text-xs text-red-400" role="alert">The first day is after the second.</p>}
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={!isActive(value)}>Clear</Button>
          <Button size="sm" onClick={applyRange} disabled={bad || !(from || to)}>Apply range</Button>
        </div>
      </div>
    </div>
  );
}

/* ---- values ---- */

const shown = (v) => (typeof v === 'boolean' ? (v ? 'True' : 'False') : v === '' ? '(empty)' : String(v));
const sameValue = (a, b) => typeof a === typeof b && a === b;

function ValuesPanel({ def, value, onChange, tiles }) {
  const [q, setQ] = useState('');
  const [state, setState] = useState({ status: 'loading', values: [], truncated: false });
  const sources = useMemo(() => valueSources(def, tiles), [def, tiles]);
  const sourcesKey = JSON.stringify(sources);
  const selected = value || [];

  useEffect(() => {
    const list = JSON.parse(sourcesKey);
    if (!list.length) { setState({ status: 'empty', values: [], truncated: false }); return undefined; }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading' }));
    const timer = setTimeout(() => {
      Promise.allSettled(list.map((s) => fieldValues(s.datasetId, s.field, { q: q.trim(), signal: ctrl.signal })))
        .then((results) => {
          if (ctrl.signal.aborted) return;
          const ok = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
          if (!ok.length) { setState({ status: 'error', values: [], truncated: false }); return; }
          setState({ status: 'ready', ...mergeValues(ok) });
        });
    }, q ? SEARCH_DEBOUNCE_MS : 0);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [sourcesKey, q]);

  const toggle = (v) => {
    const on = selected.some((s) => sameValue(s, v));
    const next = on ? selected.filter((s) => !sameValue(s, v)) : [...selected, v];
    onChange(next.length ? next.slice(0, MAX_VALUES) : null);
  };
  // What is picked stays listed even when the search hides it.
  const extra = selected.filter((s) => !state.values.some((v) => sameValue(v, s)));
  const list = [...extra, ...state.values];

  return (
    <div>
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ui-text-muted)]" aria-hidden="true" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search values" aria-label="Search values"
          className={`${FIELD} pl-8`} />
      </label>
      <div className="mt-2 max-h-64 overflow-y-auto" role="group" aria-label={`${def.label} values`}>
        {state.status === 'loading' && !list.length && (
          <p className="flex items-center gap-2 px-2 py-3 text-xs text-[var(--ui-text-muted)]" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading values
          </p>
        )}
        {state.status === 'empty' && <p className="px-2 py-3 text-xs text-[var(--ui-text-muted)]">No chart on this dashboard uses this filter's field.</p>}
        {state.status === 'error' && <p className="px-2 py-3 text-xs text-[var(--ui-text-muted)]">The values couldn't be loaded.</p>}
        {state.status === 'ready' && !list.length && <p className="px-2 py-3 text-xs text-[var(--ui-text-muted)]">{q ? 'No values start with that.' : 'No values.'}</p>}
        {list.map((v) => {
          const on = selected.some((s) => sameValue(s, v));
          return (
            <button key={`${typeof v}:${v}`} type="button" role="checkbox" aria-checked={on} className={OPTION} onClick={() => toggle(v)}>
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded ${on ? 'bg-white text-black' : 'bg-[var(--ui-inset)]'}`}>
                {on && <Check className="h-3 w-3" aria-hidden="true" />}
              </span>
              <span className="truncate">{shown(v)}</span>
            </button>
          );
        })}
      </div>
      {state.truncated && <p className="mt-1 px-2 text-[11px] text-[var(--ui-text-muted)]">Showing the first {MAX_VALUES}. Type to find others.</p>}
      <div className="mt-2 flex items-center justify-between border-t border-[var(--ui-divider)] pt-2">
        <span className="px-2 text-xs text-[var(--ui-text-muted)]">{selected.length ? `${selected.length} selected` : 'All values'}</span>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={!selected.length}>Clear</Button>
      </div>
    </div>
  );
}

/* ---- text ---- */

function TextPanel({ value, onChange }) {
  const [text, setText] = useState(value || '');
  const apply = () => onChange(text.trim() ? text : null);
  return (
    <form onSubmit={(e) => { e.preventDefault(); apply(); }}>
      <label className="mb-1.5 block text-xs text-[var(--ui-text-muted)]" htmlFor="filter-text">Contains</label>
      <div className="flex gap-2">
        <input id="filter-text" type="text" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} className={FIELD} autoComplete="off" />
        <Button size="sm" type="submit">Apply</Button>
      </div>
      <Footer onClear={() => { setText(''); onChange(null); }} active={isActive(value)} />
    </form>
  );
}

/* ---- one filter ---- */

function FilterButton({ def, value, onChange, tiles, editing, onEdit, onRemove }) {
  const [open, setOpen] = useState(false);
  const active = isActive(value);
  const set = (v) => { onChange(def.id, v); if (def.kind !== 'values') setOpen(false); };
  return (
    <span className="inline-flex max-w-full items-center gap-0.5" data-filter-id={def.id}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button"
            className={`inline-flex h-8 max-w-[22rem] items-center gap-1.5 rounded-full px-3 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${active ? 'bg-white/[0.14] text-[var(--ui-text)]' : 'bg-[var(--ui-inset)] text-[var(--ui-text-muted)] hover:text-[var(--ui-text)]'}`}>
            <span className="shrink-0 font-medium text-[var(--ui-text)]">{def.label}</span>
            <span className="truncate">{valueSummary(def, value)}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={PANEL} sideOffset={6} align="start" collisionPadding={16}>
            {def.kind === 'date' && <DatePanel value={value} onChange={set} />}
            {def.kind === 'values' && <ValuesPanel def={def} value={value} onChange={set} tiles={tiles} />}
            {def.kind === 'text' && <TextPanel value={value} onChange={set} />}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {editing && (
        <span className="flex items-center" data-no-drag="">
          <button type="button" className={ICON_BUTTON} onClick={() => onEdit(def)} aria-label={`Edit the filter ${def.label}`} title="Edit filter">
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button type="button" className={ICON_BUTTON} onClick={() => onRemove(def)} aria-label={`Remove the filter ${def.label}`} title="Remove filter">
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      )}
    </span>
  );
}

/**
 * defs       the dashboard's filter definitions (the draft's, in edit mode)
 * values     { id: value | null } the bar shows (filterState.effectiveValues)
 * onChange(id, value), onReset(), canReset
 * tiles      the dashboard's tiles (where a values list is read from)
 * editing, onAdd(), onEdit(def), onRemove(def)
 */
export default function FilterBar({ defs, values, onChange, onReset, canReset, tiles, editing = false, onAdd, onEdit, onRemove }) {
  if (!defs.length && !editing) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[22px] bg-[var(--ui-panel)] px-4 py-2.5 shadow-[inset_0_1px_0_var(--ui-panel-highlight)]"
      role="region" aria-label="Filters">
      {defs.map((d) => (
        <FilterButton key={d.id} def={d} value={values[d.id] ?? null} onChange={onChange} tiles={tiles}
          editing={editing} onEdit={onEdit} onRemove={onRemove} />
      ))}
      {!defs.length && editing && <span className="text-xs text-[var(--ui-text-muted)]">Filters narrow every chart on the dashboard at once.</span>}
      <span className="ml-auto flex items-center gap-2">
        {canReset && (
          <Button variant="ghost" size="sm" onClick={onReset}><RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Reset</Button>
        )}
        {editing && (
          <Button variant="secondary" size="sm" onClick={onAdd} disabled={defs.length >= MAX_FILTERS}
            title={defs.length >= MAX_FILTERS ? `A dashboard holds at most ${MAX_FILTERS} filters.` : undefined}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add filter
          </Button>
        )}
      </span>
    </div>
  );
}
